import axios, { AxiosResponse } from 'axios'

import { Locale } from '@lib/language'
import {
  CreatePrintShopResponse,
  DeactivatePrintShopPayload,
  DeactivatePrintShopResponse,
  RemoveShopPrintPayload,
  RemoveShopPrintResponse,
  UpdatePrintShopPayload,
  UpdatePrintShopResponse,
  UpdateShopPrintPayload,
  UpdateShopPrintResponse,
  UploadShopPrintResponse,
} from './types'

// Maximum print upload file size: 5MB
export const maximumPrintFileSize = 5 * 1024 * 1024

/**
 * Sends a request to create a new shop.
 */
export const createShop = async (locale: Locale) => {
  const createPrintShopResponse = await axios.post<
    CreatePrintShopResponse,
    AxiosResponse<CreatePrintShopResponse>
  >(
    '/api/shop/create',
    {},
    {
      headers: {
        'X-Locale': locale,
      },
    },
  )

  const shop = createPrintShopResponse.data.shop

  if (!shop) {
    throw new Error('Failed to create shop')
  }

  return shop
}

/**
 * Sends a request to update a print shop.
 */
export const updateShop = async (
  locale: Locale,
  payload: UpdatePrintShopPayload,
) => {
  const updatePrintShopResponse = await axios.post<
    UpdatePrintShopResponse,
    AxiosResponse<UpdatePrintShopResponse>,
    UpdatePrintShopPayload
  >('/api/shop/update', payload, {
    headers: {
      'X-Locale': locale,
    },
  })

  const shop = updatePrintShopResponse.data.shop
  const newPrints = updatePrintShopResponse.data.newPrints ?? []

  if (!shop) {
    throw new Error('Failed to update and activate shop')
  }

  return [shop, newPrints] as const
}

/**
 * Sends a request to deactivate a shop.
 */
export const deactivateShop = async (
  locale: Locale,
  payload: DeactivatePrintShopPayload,
) => {
  const deactivatePrintShopResponse = await axios.post<
    DeactivatePrintShopResponse,
    AxiosResponse<DeactivatePrintShopResponse>,
    DeactivatePrintShopPayload
  >('/api/shop/deactivate', payload, {
    headers: {
      'X-Locale': locale,
    },
  })

  if (deactivatePrintShopResponse.data.error) {
    throw new Error(deactivatePrintShopResponse.data.error)
  }
}

/**
 * Uploads new prints for a shop.
 */
export const uploadShopPrints = async (locale: Locale, formData: FormData) => {
  const uploadShopPrintResponse = await axios.post<
    UploadShopPrintResponse,
    AxiosResponse<UploadShopPrintResponse>,
    FormData
  >('/api/shop/prints/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      'X-Locale': locale,
    },
  })

  if (uploadShopPrintResponse.data.error) {
    throw new Error(uploadShopPrintResponse.data.error)
  }

  const newPrints = uploadShopPrintResponse.data.newPrints ?? []

  return newPrints
}

/**
 * Updates a shop print title.
 */
export const updateShopPrint = async (
  locale: Locale,
  shopId: string,
  printId: string,
  title: string,
) => {
  const updateShopPrintResponse = await axios.post<
    UpdateShopPrintResponse,
    AxiosResponse<UpdateShopPrintResponse>,
    UpdateShopPrintPayload
  >(
    '/api/shop/prints/update',
    {
      shopId,
      printId,
      title,
    },
    {
      headers: {
        'X-Locale': locale,
      },
    },
  )

  if (updateShopPrintResponse.data.error) {
    throw new Error(updateShopPrintResponse.data.error)
  }
}

/**
 * Removes a shop print and all other shop prints containing the same asset.
 */
export const removeShopPrint = async (
  locale: Locale,
  shopId: string,
  printId: string,
) => {
  const removeShopPrintResponse = await axios.post<
    RemoveShopPrintResponse,
    AxiosResponse<RemoveShopPrintResponse>,
    RemoveShopPrintPayload
  >(
    '/api/shop/prints/remove',
    {
      shopId,
      printId,
    },
    {
      headers: {
        'X-Locale': locale,
      },
    },
  )

  if (removeShopPrintResponse.data.error) {
    throw new Error(removeShopPrintResponse.data.error)
  }
}
